import { useCallback } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import type { Snapshot } from 'recoil';
import clone from 'ramda/es/clone';
import { steppedHPConfigurations, clinicalStepsBaseline, getStepsFromVersioned } from '../state';
import {
  clinicalSnapshotStepSmids,
  deleteClinicalSnapshot,
  getClinicalSnapshot,
  hasClinicalSnapshot,
  revertStepsToBaseline,
  setClinicalSnapshot,
  stageStepEdit,
  stageStepTransform,
  StepWriteOrigin,
  useClinicalStepWriters,
} from '../clinicalStepStore';
import {
  viewportsConfigurationsState,
  dehydratedHangingProtocol,
  activeHangingProtocolSmidState,
} from '../../ViewportsConfigurations/state';
import {
  applyProtocolLayouts,
  restoreUserLayoutOverride,
} from '../../Viewer/StudyLoader/viewerLayoutWriters';
import { layoutsForAllCasesState } from '../../Viewer/StudyLoader/viewerLoaderState';
import type { Layouts } from '../../Viewer/StudyLoader/viewerLoaderState';
import type { DehydratedViewportsConfigurations } from '../../ViewportsConfigurations/types';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { clearViewportOverrides } from '../../ViewportsConfigurations/manipulators';
import { useToasterDispatch } from 'common/ui/Toaster/Toaster';
import { createStepToastContentComponent, getDefaultHPToastOptions } from '../Steps/StepToast';
import { HP_TOAST_UNDO_DURATION } from '../Steps/hpStepToastMessages';
import { CLINICAL_STEP_REVERTED_TOAST } from '../Steps/stepsToastFactory';
import { formatLayoutString } from '../steppedHPTypes';
import type { ViewportConfigurationEntry } from '../steppedHPTypes';
import { stepViewportConfigsToDehydrated, stepLayoutsToLayouts } from './useStepFormSync';
import { useClinicalViewportClassification } from './useClinicalViewportClassification';

/**
 * The smid of the step currently at `stepIndex`.
 *
 * Callers still address steps by index -- that is what the step bar, the drag handlers and the
 * mouse adaptors have -- so the index is resolved to an identity here, at the boundary, rather than
 * pushed out to every call site. Reads the snapshot rather than subscribing: this hook mounts in
 * three places and none of them should re-render because some other step changed.
 */
function stepSmidAt(snapshot: Snapshot, stepIndex: number): Nullable<string> {
  const versioned = snapshot.getLoadable(steppedHPConfigurations).getValue();
  return getStepsFromVersioned(versioned)?.[stepIndex]?.smid ?? null;
}

export function useClinicalViewportEditing() {
  const { currentCaseId } = useCurrentCaseContext();
  const activeHPSmid = useRecoilValue(activeHangingProtocolSmidState);
  const { stageStepAt } = useClinicalStepWriters();
  const { enqueueToast } = useToasterDispatch();

  const { classify, pendingClassifications, cancelForStep } = useClinicalViewportClassification();

  // ── Snapshot management ──────────────────────────────────────────────

  const clearSnapshot = useRecoilCallback(
    ({ snapshot }) =>
      (stepIndex: number) => {
        const stepSmid = stepSmidAt(snapshot, stepIndex);
        deleteClinicalSnapshot(currentCaseId, activeHPSmid, stepSmid);
      },
    [currentCaseId, activeHPSmid]
  );

  const hasSnapshot = useRecoilCallback(
    ({ snapshot }) =>
      (stepIndex: number): boolean => {
        const stepSmid = stepSmidAt(snapshot, stepIndex);
        return hasClinicalSnapshot(currentCaseId, activeHPSmid, stepSmid);
      },
    [currentCaseId, activeHPSmid]
  );

  const ensureSnapshot = useRecoilCallback(
    ({ snapshot }) =>
      (stepIndex: number) => {
        const caseId = currentCaseId;
        if (!caseId) return;

        const stepSmid = stepSmidAt(snapshot, stepIndex);
        if (hasClinicalSnapshot(caseId, activeHPSmid, stepSmid)) return;

        // Synchronous reads so the snapshot is written before the caller's
        // next write call in the same tick (getPromise would defer to next microtask).
        const vpState = snapshot.getLoadable(viewportsConfigurationsState(caseId)).getValue();
        const allCaseLayouts = snapshot.getLoadable(layoutsForAllCasesState).getValue();
        const caseLayouts = allCaseLayouts[caseId] ?? null;

        setClinicalSnapshot(caseId, activeHPSmid, stepSmid, {
          viewports: vpState?.data ? clone(vpState.data) : null,
          layouts: caseLayouts ? clone(caseLayouts) : null,
        });
      },
    [currentCaseId, activeHPSmid]
  );

  // ── Step atom writers ────────────────────────────────────────────────

  const writeViewportToStep = useCallback(
    (stepIndex: number, viewportId: string, entry: ViewportConfigurationEntry) => {
      stageStepAt(stepIndex, (step) => ({
        ...step,
        viewports: {
          ...step.viewports,
          [viewportId]: entry,
        },
      }));
    },
    [stageStepAt]
  );

  /**
   * Clears one viewport within a step by removing its entry from the step's `viewports` record.
   *
   * An absent entry is how the clinical render path already represents a cleared slot:
   * `rebuildHPFromStep` fills every layout slot with no step entry using
   * `CLEARED_VIEWPORT_CONFIGURATION`, and `viewportsRecordToInputArray` drops cleared entries at
   * save time, so removal round-trips through the backend as "this slot is empty". Writing an entry
   * with null smids instead would leave `series_criteria_source_text` behind for the matcher to
   * re-hang a series into the slot the user just cleared.
   *
   * Being a step write, this also makes the clear visible to the dirty-state comparison, which is
   * what puts the unsaved-changes dot on the step (VX-4733).
   */
  const clearViewportInStep = useCallback(
    (stepIndex: number, viewportId: string) => {
      stageStepAt(stepIndex, (step) => {
        if (!(viewportId in step.viewports)) return step;
        const { [viewportId]: _cleared, ...remaining } = step.viewports;
        return {
          ...step,
          viewports: remaining,
        };
      });
    },
    [stageStepAt]
  );

  const swapViewportsInStep = useCallback(
    (stepIndex: number, viewportIdA: string, viewportIdB: string) => {
      stageStepAt(stepIndex, (step) => {
        const entryA = step.viewports[viewportIdA];
        const entryB = step.viewports[viewportIdB];
        // A bad viewport id (one with no record in this step) would otherwise write
        // `undefined` into the swapped-to slot, which later crashes rebuildHPFromStep on
        // render (VX-4593). The only caller that passed a bad id is already fixed
        // (ViewportDragDropTarget's handleDrop routing), but a no-op here is a cheap second
        // layer against a future caller making the same mistake (PR #9319 review feedback).
        if (entryA === undefined || entryB === undefined) return step;
        return {
          ...step,
          viewports: {
            ...step.viewports,
            [viewportIdA]: entryB,
            [viewportIdB]: entryA,
          },
        };
      });
    },
    [stageStepAt]
  );

  /**
   * Applies a layout change to a single step in clinical mode.
   *
   * All three writes are needed, and the third is what keeps the change step-scoped:
   *
   * 1. `steppedHPConfigurations` — the edit itself, which is what the dirty-state check compares
   *    against `clinicalStepsBaseline` to light up the unsaved-changes dot.
   * 2. `hangingProtocolLayoutsState` — renders the new layout on this paint. The step atom write
   *    also reaches this atom via `useHangingProtocol`'s memo chain, but only in an effect that
   *    fires after paint, so without the direct write the grid renders one frame stale.
   * 3. Clearing `layoutsForAllCasesState[caseId]` — the user-override layer. It is case-scoped and
   *    `localForage`-persisted, and `useLayouts` gives it priority over the step-derived HP
   *    layouts, so leaving a step's layout there applied it to *every* step and survived a reload
   *    as an edit no step was ever marked dirty for (VX-4624). In clinical step mode the active
   *    step owns the layout, so this layer must stay empty.
   */
  const writeLayoutToStep = useRecoilCallback(
    ({ set }) =>
      (stepIndex: number, layouts: Layouts) => {
        stageStepTransform(set, {
          stepIndex,
          origin: StepWriteOrigin.LocalUser,
          transform: (step) => {
            const stepLayouts: Record<string, string> = {};
            for (const [windowId, tuple] of Object.entries(layouts)) {
              stepLayouts[windowId] = formatLayoutString(tuple);
            }
            return { ...step, layouts: stepLayouts };
          },
        });

        applyProtocolLayouts(set, { caseId: currentCaseId, layouts });
      },
    [currentCaseId]
  );

  // ── Revert ───────────────────────────────────────────────────────────

  const revertWithSnapshot = useRecoilCallback(
    ({ set, snapshot: recoilSnapshot }) =>
      (stepIndex: number) => {
        const stepSmid = stepSmidAt(recoilSnapshot, stepIndex);
        const snap = getClinicalSnapshot(currentCaseId, activeHPSmid, stepSmid);
        if (!snap) return;

        // Read baseline synchronously so all set() calls remain in the same
        // Recoil batch. Using getPromise (async) would break the batch boundary,
        // causing dehydratedHangingProtocol to be stale during the first render.
        const baseline = recoilSnapshot.getLoadable(clinicalStepsBaseline).getValue();

        if (baseline?.[stepIndex]) {
          revertStepsToBaseline(set, { indices: new Set([stepIndex]), baseline });

          // Write the baseline step's viewport configs directly to the
          // dehydratedHangingProtocol atom. The step atom update above triggers
          // useHangingProtocol's memo chain, but the effect that writes the
          // recalculated value to dehydratedHangingProtocol fires AFTER paint.
          // Without this direct write, the viewport selector reads the stale
          // atom during the first render and the viewport keeps showing the
          // dropped series instead of reverting to the original.
          if (currentCaseId) {
            set(
              dehydratedHangingProtocol(currentCaseId),
              stepViewportConfigsToDehydrated(baseline[stepIndex].viewports)
            );
          }
        }

        // Reset user viewport overrides to empty (not null). With data: {}, the
        // factory merge produces no user overrides.
        const caseId = currentCaseId;
        if (caseId) {
          set(viewportsConfigurationsState(caseId), clearViewportOverrides);
        }

        // Restore layout atoms from snapshot. When the snapshot has no layout
        // data (layoutsForAllCasesState was empty at snapshot time because the
        // initial layout only lived in hangingProtocolLayoutsState), fall back
        // to deriving layouts from the baseline step data.
        const restoredLayouts = snap.layouts
          ? clone(snap.layouts)
          : baseline?.[stepIndex]?.layouts
            ? stepLayoutsToLayouts(baseline[stepIndex].layouts)
            : null;
        if (restoredLayouts) {
          if (snap.layouts) {
            // The step had a case-scoped override before it was edited (e.g. a layout the user set
            // outside step mode). Put it back exactly as it was.
            restoreUserLayoutOverride(set, { caseId, layouts: restoredLayouts });
          } else {
            // No override existed before the edit, so the layout came from the step. Restoring one
            // here would re-introduce the cross-step, reload-surviving leak (VX-4624); let the
            // step-derived layouts govern instead.
            applyProtocolLayouts(set, { caseId, layouts: restoredLayouts });
          }
        }

        // Cancel any in-flight classifications for this step
        cancelForStep(stepIndex);

        // Remove the snapshot since we've reverted
        deleteClinicalSnapshot(currentCaseId, activeHPSmid, stepSmid);
      },
    [currentCaseId, activeHPSmid, cancelForStep]
  );

  const revertWithToast = useRecoilCallback(
    ({ snapshot: recoilSnapshot, set }) =>
      (stepIndex: number) => {
        const stepSmid = stepSmidAt(recoilSnapshot, stepIndex);
        const snap = getClinicalSnapshot(currentCaseId, activeHPSmid, stepSmid);
        if (!snap) return;

        const caseId = currentCaseId;

        // Capture the full modified state before reverting (for undo).
        // Use synchronous getLoadable to keep all set() calls in the same
        // Recoil batch (async getPromise breaks the batch boundary).
        const versioned = recoilSnapshot.getLoadable(steppedHPConfigurations).getValue();
        const modifiedStep = versioned.steps?.[stepIndex]
          ? clone(versioned.steps[stepIndex])
          : null;

        // Capture pre-revert viewport and layout state so UNDO can restore them
        let modifiedViewports: DehydratedViewportsConfigurations | null = null;
        let modifiedLayouts: Layouts | null = null;
        if (caseId) {
          const vpState = recoilSnapshot
            .getLoadable(viewportsConfigurationsState(caseId))
            .getValue();
          modifiedViewports = vpState?.data ? clone(vpState.data) : null;
          const allCaseLayouts = recoilSnapshot.getLoadable(layoutsForAllCasesState).getValue();
          modifiedLayouts = allCaseLayouts[caseId] ? clone(allCaseLayouts[caseId]) : null;
        }

        revertWithSnapshot(stepIndex);

        enqueueToast(CLINICAL_STEP_REVERTED_TOAST.message, {
          ...getDefaultHPToastOptions({ autoHideDuration: HP_TOAST_UNDO_DURATION }),
          ContentComponent: createStepToastContentComponent({
            actionLabel: CLINICAL_STEP_REVERTED_TOAST.actionLabel,
            countdownDuration: HP_TOAST_UNDO_DURATION,
            onAction: () => {
              // Restore the modified step back into the step atom
              if (modifiedStep) {
                stageStepEdit(set, {
                  stepIndex,
                  step: modifiedStep,
                  origin: StepWriteOrigin.LocalUser,
                });
              }

              // Restore viewport and layout atoms to pre-revert state
              if (caseId && modifiedViewports) {
                set(viewportsConfigurationsState(caseId), (prev) => ({
                  ...prev,
                  data: modifiedViewports,
                }));
              }
              if (caseId && modifiedLayouts) {
                // modifiedStep above carries the undone layout back into the step atom, so the
                // protocol layer alone is enough to render it -- no user override needed.
                applyProtocolLayouts(set, { caseId, layouts: modifiedLayouts });
              }

              // Restore the snapshot ref so future reverts still work
              setClinicalSnapshot(currentCaseId, activeHPSmid, stepSmid, snap);

              // Re-fire classifications for viewports missing criteria
              if (modifiedStep) {
                for (const [vpId, vp] of Object.entries(modifiedStep.viewports)) {
                  if (!vp.criteria?.length && vp.series_criteria_source_text) {
                    classify({
                      viewportId: vpId,
                      stepIndex,
                      seriesDescription: vp.series_criteria_source_text,
                      seriesSmid: vp.series_smid ?? null,
                    });
                  }
                }
              }
            },
          }),
        });
      },
    [revertWithSnapshot, enqueueToast, classify, currentCaseId, activeHPSmid]
  );

  const revertAll = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        const steps = getStepsFromVersioned(
          snapshot.getLoadable(steppedHPConfigurations).getValue()
        );
        if (steps == null) return;

        // Snapshots are addressed by step smid, so the index `revertWithSnapshot` wants is looked up
        // against the current order. A snapshot whose step is gone -- deleted while dirty -- has no
        // index and is skipped; reverting it would have to guess a position. The store scopes the
        // sweep to this case + HP.
        for (const stepSmid of clinicalSnapshotStepSmids(currentCaseId, activeHPSmid)) {
          const stepIndex = steps.findIndex((step) => (step.smid ?? '__no_step__') === stepSmid);
          if (stepIndex !== -1) revertWithSnapshot(stepIndex);
        }
      },
    [revertWithSnapshot, currentCaseId, activeHPSmid]
  );

  return {
    // Snapshot management
    ensureSnapshot,
    clearSnapshot,
    hasSnapshot,

    // Step atom writers
    writeViewportToStep,
    clearViewportInStep,
    swapViewportsInStep,
    writeLayoutToStep,

    // Revert
    revertWithSnapshot,
    revertWithToast,
    revertAll,

    // Classification passthrough
    pendingClassifications,
    classify,
    cancelForStep,
  };
}
